import { marked } from 'marked'
import DOMPurify from 'dompurify'

// Configure marked
marked.setOptions({
  breaks: true,      // 单个 \n 也渲染成 <br>，符合聊天消息预期
  gfm: true,         // GitHub Flavored Markdown
})

// Custom renderer for code blocks with copy button
const renderer = new marked.Renderer()
renderer.code = function (token) {
  // marked v17+ 传对象 { text, lang, escaped }
  const code = typeof token === 'object' ? (token.text || '') : token
  const language = (typeof token === 'object' ? token.lang : arguments[1]) || 'plaintext'
  const escaped = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return `<div class="md-code-block">
    <div class="md-code-header">
      <span class="md-code-lang">${language}</span>
      <button class="md-code-copy" onclick="(function(btn){var code=btn.closest('.md-code-block').querySelector('code');navigator.clipboard&&navigator.clipboard.writeText(code.innerText).then(function(){btn.textContent='已复制';setTimeout(function(){btn.textContent='复制'},1500)})})(this)" type="button">复制</button>
    </div>
    <pre><code class="language-${language}">${escaped}</code></pre>
  </div>`
}

// 链接：文件链接直接下载，其他链接新窗口打开
renderer.link = function (token) {
  const href = typeof token === 'object' ? token.href : token
  const title = typeof token === 'object' ? token.title : arguments[1]
  const text = typeof token === 'object' ? token.text : arguments[2]
  const titleAttr = title ? ` title="${title}"` : ''
  const isFile = /\.(mp3|wav|ogg|aac|flac|mp4|webm|avi|mov|pdf|doc|docx|xls|xlsx|ppt|pptx|txt|md|zip|rar|7z|png|jpg|jpeg|gif|svg|webp|csv|json)(\?|$)/i.test(href || '')
  if (isFile) {
    return `<a href="${href || ''}"${titleAttr} onclick="(function(e){e.preventDefault();fetch(e.target.href).then(function(r){return r.blob()}).then(function(b){var u=URL.createObjectURL(b),a=document.createElement('a');a.href=u;a.download='${(text || '').replace(/'/g, '')}';a.click();URL.revokeObjectURL(u)}).catch(function(){window.open(e.target.href)})})(event)" style="cursor:pointer;color:#5B8DEF;">${text || ''}</a>`
  }
  return `<a href="${href || ''}"${titleAttr} target="_blank" rel="noopener noreferrer">${text || ''}</a>`
}

marked.setOptions({ renderer })

const URL_REGEX = /(?<![("'>])(https?:\/\/[^\s<>"')\]]+)/g

// 把 URL 末尾粘连的中英文标点剥离出来，返回 { url, trail }
// 规则：对 ASCII 标点，反复剥离直到尾字符不是标点；括号只在"左右不成对"时才剥离（避免误伤 Wiki 样式 URL 里的合法括号）
const TAIL_PUNCT = /[.,!?;:，。！？；：、【】…—·]$/
function splitTrailingPunct(url) {
  let trail = ''
  while (url.length > 0) {
    const last = url[url.length - 1]
    if (TAIL_PUNCT.test(last)) {
      trail = last + trail
      url = url.slice(0, -1)
      continue
    }
    // 右括号：只在整个 url 里左括号少于右括号（多出来的右括号肯定不属于 url）时剥离
    if (last === ')' || last === '）' || last === ']' || last === '】') {
      const opens = (url.match(last === ')' ? /\(/g : last === ']' ? /\[/g : last === '）' ? /（/g : /【/g) || []).length
      const closes = (url.match(last === ')' ? /\)/g : last === ']' ? /\]/g : last === '）' ? /）/g : /】/g) || []).length
      if (closes > opens) {
        trail = last + trail
        url = url.slice(0, -1)
        continue
      }
    }
    break
  }
  return { url, trail }
}

function linkifyPlain(text) {
  return text.replace(URL_REGEX, (url) => {
    const { url: clean, trail } = splitTrailingPunct(url)
    return `<a href="${clean}" target="_blank" rel="noopener noreferrer">${clean}</a>${trail}`
  })
}

export function linkifyText(text) {
  if (!text) return ''
  // Escape HTML first to prevent XSS, then linkify
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>')
  return escaped.replace(/https?:\/\/[^\s<>"')\]]+/g, (url) => {
    const { url: clean, trail } = splitTrailingPunct(url)
    return `<a href="${clean}" target="_blank" rel="noopener noreferrer">${clean}</a>${trail}`
  })
}

const QT_RE = /<quick-tasks>([\s\S]*?)<\/quick-tasks>/g
const TASK_RE = /<task\s+([^>]*?)\/?\s*>/g
function attrVal(attrs, name) {
  const m = attrs.match(new RegExp(name + '=["\']([^"\']*)["\']'))
  return m ? m[1] : ''
}
function renderQuickTasks(text) {
  return text.replace(QT_RE, (_, inner) => {
    let cards = ''
    let m
    TASK_RE.lastIndex = 0
    while ((m = TASK_RE.exec(inner)) !== null) {
      const attrs = m[1]
      const icon = attrVal(attrs, 'icon') || '⚡'
      const title = attrVal(attrs, 'title') || ''
      const desc = attrVal(attrs, 'desc') || ''
      const action = attrVal(attrs, 'action') || title
      cards += `<button type="button" class="qt-card" data-qt-action="${action.replace(/"/g, '&quot;')}"><span class="qt-icon">${icon}</span><span class="qt-text"><span class="qt-title">${title}</span><span class="qt-desc">${desc}</span><span class="qt-go">立即体验</span></span></button>`
    }
    return `<div class="qt-row">${cards}</div>`
  })
}

// ── 信息卡片 <info-card>（技能更新 / 定时任务结果 等）──────────────
// 后端下发：
// <info-card icon="⚠️" title="技能更新" accent="amber">
//   <row label="技能" value="接口巡检分析" />
//   <row label="最新版本" value="v2.3.0" color="green" />
//   <row label="更新日志" value="移除…·新增…" />
//   <action text="🔄 立即更新到 v2.3.0" do="把接口巡检分析更新到 v2.3.0" />
// </info-card>
// 渲染成 .ic-card；<action> 复用 data-qt-action（已在 DOMPurify 白名单 + _onQuickTask 处理）
const IC_RE = /<info-card\s+([^>]*?)>([\s\S]*?)<\/info-card>/g
const IC_ROW_RE = /<row\s+([^>]*?)\/?\s*>/g
const IC_ACTION_RE = /<action\s+([^>]*?)\/?\s*>/g
const IC_FILE_RE = /<file\s+([^>]*?)\/?\s*>/g
// 文件名 → 图标角标(label + tone class)，复用 .telegram-file-card 同款配色
function icFileIcon(name) {
  const ext = ((name || '').split('.').pop() || '').toLowerCase()
  const map = {
    pdf: ['PDF', 'tg-icon-pdf'], doc: ['DOC', 'tg-icon-doc'], docx: ['DOC', 'tg-icon-doc'],
    xls: ['XLS', 'tg-icon-xls'], xlsx: ['XLS', 'tg-icon-xls'], csv: ['CSV', 'tg-icon-xls'],
    ppt: ['PPT', 'tg-icon-ppt'], pptx: ['PPT', 'tg-icon-ppt'],
    zip: ['ZIP', 'tg-icon-zip'], rar: ['ZIP', 'tg-icon-zip'], '7z': ['ZIP', 'tg-icon-zip'],
    md: ['MD', 'tg-icon-txt'], txt: ['TXT', 'tg-icon-txt'], json: ['JSON', 'tg-icon-txt'],
    html: ['HTML', 'tg-icon-html'], htm: ['HTML', 'tg-icon-html']
  }
  return map[ext] || [(ext || '文').toUpperCase().slice(0, 4), 'tg-icon-default']
}
// 刷新图标（<action icon="refresh">），与原型一致
const IC_REFRESH_SVG = '<svg viewBox="0 0 24 24" fill="none"><path d="M21 2v6h-6M3 12a9 9 0 0115-6.7L21 8M3 22v-6h6M21 12a9 9 0 01-15 6.7L3 16" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"></path></svg>'
function renderInfoCards(text) {
  return text.replace(IC_RE, (_, headAttrs, inner) => {
    const icon = attrVal(headAttrs, 'icon') || '📋'
    const title = attrVal(headAttrs, 'title') || ''
    let rows = ''
    let m
    IC_ROW_RE.lastIndex = 0
    while ((m = IC_ROW_RE.exec(inner)) !== null) {
      const a = m[1]
      const label = attrVal(a, 'label') || ''
      const value = attrVal(a, 'value') || ''
      const color = (attrVal(a, 'color') || '').replace(/[^a-z]/gi, '')
      const colorCls = color ? ` ic-${color}` : ''
      rows += `<div class="ic-row"><span class="ic-label">${label}</span><span class="ic-value${colorCls}">${value}</span></div>`
    }
    let actions = ''
    IC_ACTION_RE.lastIndex = 0
    while ((m = IC_ACTION_RE.exec(inner)) !== null) {
      const a = m[1]
      const atext = attrVal(a, 'text') || '立即处理'
      const ado = attrVal(a, 'do') || atext
      const aicon = attrVal(a, 'icon') === 'refresh' ? IC_REFRESH_SVG : ''
      const askill = attrVal(a, 'skill-id') || attrVal(a, 'skillid') || ''
      const skillAttr = askill ? ` data-skill-id="${askill.replace(/"/g, '&quot;')}"` : ''
      actions += `<button type="button" class="ic-action" data-qt-action="${ado.replace(/"/g, '&quot;')}"${skillAttr}>${aicon}${atext}</button>`
    }
    // 可选文件卡（定时任务交付物等）：复用 .telegram-file-card 样式，有 url 则可点开
    let fileCard = ''
    IC_FILE_RE.lastIndex = 0
    const fm = IC_FILE_RE.exec(inner)
    if (fm) {
      const fa = fm[1]
      const fname = attrVal(fa, 'name') || '文件'
      const fmeta = attrVal(fa, 'meta') || ''
      const furl = attrVal(fa, 'url') || ''
      const [flabel, fcls] = icFileIcon(fname)
      const inner2 = `<div class="telegram-file-top"><div class="telegram-file-icon ${fcls}">${flabel}</div><div class="telegram-file-copy"><div class="telegram-file-title">${fname}</div><div class="telegram-file-meta">${fmeta}</div></div></div>`
      fileCard = furl
        ? `<a class="telegram-file-card ic-file" href="${furl}" target="_blank" rel="noopener noreferrer">${inner2}</a>`
        : `<div class="telegram-file-card ic-file">${inner2}</div>`
    }
    // 有文件卡时，下方字段块加虚线分隔（与原型定时任务卡一致）
    const blockCls = fileCard ? 'ic-block ic-divided' : 'ic-block'
    // 卡片本体无边框/底色（白底圆角由 agent 气泡提供，与原型一致）
    return `<div class="ic-card">${fileCard}<div class="${blockCls}"><div class="ic-head"><span class="ic-icon">${icon}</span><span class="ic-title">${title}</span></div>${rows}</div>${actions}</div>`
  })
}

export function renderMarkdown(text) {
  if (!text) return ''
  try {
    const processed = renderInfoCards(renderQuickTasks(text))
    let html = marked.parse(processed)
    // 每个 <table> 包一层 .md-table-wrap，让表格独立横向滚动（不依赖外层 .md-content 的 overflow）
    html = html.replace(/<table([\s\S]*?)<\/table>/g, '<div class="md-table-wrap"><table$1</table></div>')
    // After markdown parse, linkify any bare URLs that marked didn't convert
    const linked = html.replace(/>([^<]+)</g, (match, inner) => {
      // Only linkify text nodes that don't already contain href
      return '>' + inner.replace(URL_REGEX, (url) => {
        const { url: clean, trail } = splitTrailingPunct(url)
        return `<a href="${clean}" target="_blank" rel="noopener noreferrer">${clean}</a>${trail}`
      }) + '<'
    })
    return DOMPurify.sanitize(linked, {
      ALLOWED_TAGS: ['p','br','strong','em','del','h1','h2','h3','h4','h5','h6',
        'ul','ol','li','blockquote','hr','a','code','pre','div','span','button','table','thead','tbody','tr','th','td',
        'svg','path'],
      ALLOWED_ATTR: ['href','target','rel','class','type','onclick','data-qt-action','data-skill-id',
        'viewBox','fill','stroke','stroke-width','stroke-linecap','stroke-linejoin','d'],
      ALLOW_DATA_ATTR: false,
    })
  } catch (e) {
    return text
  }
}

// 把 markdown 标记剥成纯文本，用于侧栏/搜索结果等单行预览
// 优先处理"撑爆型"块（表格/代码块/图片）→ 用语义占位符 [表格 N×M] [代码 lang] [图片] 替代
// 其余 markdown 标记（标题/加粗/链接 等）剥成纯文本
export function stripMarkdown(text) {
  if (!text) return ''
  let s = String(text)

  // ── 0a. 自定义卡片标签 → 友好标签(必须在通用 HTML 剥离之前,否则 IC_RE/QT_RE 先被吃)──
  s = s.replace(IC_RE, (_, headAttrs) => ' [' + (attrVal(headAttrs, 'title') || '卡片') + '] ')
  s = s.replace(QT_RE, ' [快捷任务] ')

  // ── 0b. 通用 HTML 标签剥离(系统/官方会话消息体本身就是 HTML,预览里别露 <a href=...>) ──
  //   只匹配 < 后跟字母的合法 tag,避开聊天里 "<3 我爱你" / "x < y" 这种文本误伤
  s = s.replace(/<\/?[a-zA-Z][^>]*>/g, '')
  //   常见 HTML 实体反转义,让 &amp;/&lt; 等回到原字符
  s = s.replace(/&nbsp;/g, ' ')
       .replace(/&amp;/g, '&')
       .replace(/&lt;/g, '<')
       .replace(/&gt;/g, '>')
       .replace(/&quot;/g, '"')
       .replace(/&#39;/g, "'")

  // ── 1. 撑爆型块：用语义占位符 ──
  // 表格：连续多行以 | 开头/结尾的块，提取行数和列数
  s = s.replace(/(?:^[ \t]*\|.*\|[ \t]*$\n?)+/gm, (block) => {
    const lines = block.trim().split('\n').filter(l => l.trim())
    if (lines.length === 0) return ''
    // 排除分隔行后的实际数据行数
    const dataRows = lines.filter(l => !/^\s*\|?[\s:-]+\|/.test(l)).length
    const cols = Math.max(1, (lines[0].match(/\|/g) || []).length - 1)
    return ' [表格 ' + Math.max(1, dataRows) + '×' + cols + '] '
  })

  // 代码块（带语言名）
  s = s.replace(/```(\w*)[ \t]*\n[\s\S]*?```/g, (_, lang) => ' [代码' + (lang ? ' ' + lang : '') + '] ')
  // 单行 ``` ``` 残留
  s = s.replace(/```[\s\S]*?```/g, ' [代码] ')

  // 图片：![alt](url) → alt 或 [图片]
  s = s.replace(/!\[([^\]]*)\]\([^)]*\)/g, (_, alt) => alt ? alt : ' [图片] ')

  // ── 2. 普通 markdown 标记剥离 ──
  // 行内代码
  s = s.replace(/`([^`]+)`/g, '$1')
  // 链接 [text](url) → text
  s = s.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
  // 行首符号：标题 / 引用 / 列表
  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, '')
  s = s.replace(/^\s{0,3}>\s?/gm, '')
  s = s.replace(/^\s{0,3}[-*+]\s+/gm, '')
  s = s.replace(/^\s{0,3}\d+\.\s+/gm, '')
  // hash 前缀（无空格的 #xxx ##yyy 也剥掉前置 # 序列）
  s = s.replace(/(^|\s)#{1,6}(?=\S)/g, '$1')
  // HR：独立一行的 ---/***/___（含空格的 - - - 也算），仅剥这一行
  // 之前写成 [\s\S]*$ + gm 会把 \n 也吃进去，遇到首行 --- (YAML 风) 直接清空整段
  s = s.replace(/^[ \t]{0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/gm, '')
  // 加粗/斜体/删除线
  s = s.replace(/\*\*([^*]+)\*\*/g, '$1')
  s = s.replace(/__([^_]+)__/g, '$1')
  s = s.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2')
  s = s.replace(/(^|[^_])_([^_\n]+)_/g, '$1$2')
  s = s.replace(/~~([^~]+)~~/g, '$1')

  // ── 3. 多余空白合并 ──
  s = s.replace(/\s+/g, ' ').trim()
  return s
}
